import { useState, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  fetchModulesPermissions,
  addSubAdmin,
  updateUserPermissions
} from "../../../store/slices/adminSlice";
import { fetchCountries } from "../../../store/slices/utilitySlice";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const AddSubAdmin = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { modulesPermissions, addUserLoading, permissionsLoading, updatingPermissions, myPermissions } = useAppSelector((state) => state.admin);
  const { user: loggedInUser } = useAppSelector((state) => state.auth);
  const { countries } = useAppSelector((state) => state.utility);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    country_id: "",
    country_code: "+91",
    mobile: "",
    status: "Active",
  });

  const [selectedPerms, setSelectedPerms] = useState([]);
  const [tooltipVisible, setTooltipVisible] = useState(null);

  // True when the logged-in user is a SubAdmin (not a super-admin)
  const isSubAdminUser = loggedInUser?.user_type === 'SubAdmin';

  // Set of permission IDs the logged-in SubAdmin owns — used to restrict what they can assign
  const myPermIdSet = useMemo(() => {
    if (!isSubAdminUser || !myPermissions) return null; // null = no restriction (Admin)
    return new Set(myPermissions.permission_ids.map(Number));
  }, [isSubAdminUser, myPermissions]);

  useEffect(() => {
    dispatch(fetchModulesPermissions());
    dispatch(fetchCountries());
  }, [dispatch]);

  // When a SubAdmin opens the form, pre-check all their own permissions
  useEffect(() => {
    if (isSubAdminUser && myPermissions && myPermissions.permission_ids.length > 0) {
      setSelectedPerms(myPermissions.permission_ids.map(Number));
    }
  }, [isSubAdminUser, myPermissions]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'country_id') {
      const selectedCountry = countries.find(c => c.id === parseInt(value));
      setForm({ 
        ...form, 
        country_id: value,
        country_code: selectedCountry?.phone_code || form.country_code
      });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  /**
   * Returns true if this permission checkbox is locked (read-only).
   * A permission is locked when the logged-in user is a SubAdmin and does NOT have that permission.
   */
  const isPermLocked = (permId) => {
    if (!myPermIdSet) return false; // Admin — nothing locked
    return !myPermIdSet.has(Number(permId));
  };

  // Toggle permission — silently ignore locked permissions
  const togglePermission = (permId) => {
    if (isPermLocked(permId)) return;
    const id = Number(permId);
    setSelectedPerms((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  // Check all for a module — only toggles permissions the user owns
  const toggleCheckAll = (module) => {
    const ownedIds = module.permissions
      .map(p => Number(p.id))
      .filter(id => !isPermLocked(id));

    if (ownedIds.length === 0) return;

    const allChecked = ownedIds.every(id => selectedPerms.includes(id));
    if (allChecked) {
      setSelectedPerms(prev => prev.filter(id => !ownedIds.includes(id)));
    } else {
      setSelectedPerms(prev => [...new Set([...prev, ...ownedIds])]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.first_name || !form.last_name || !form.email) {
      toast.error("Please fill all required fields");
      return;
    }

    if (selectedPerms.length === 0) {
      toast.error("Please select at least one permission");
      return;
    }

    try {
      // 1. Create SubAdmin using the specialized thunk
      const userData = {
        ...form,
        mobile_number: form.mobile,
        phone_code: form.country_code,
        country_id: form.country_id || null,
        // The specialized addSubAdmin might expect some permissions initially
        permissions: selectedPerms 
      };

      const userResponse = await dispatch(addSubAdmin(userData)).unwrap();
      const userId = userResponse?.data?.id || userResponse?.id;

      if (!userId) {
        throw new Error("Failed to get user ID from response");
      }

      // 2. Sync SAX permissions explicitly to ensure consistency
      await dispatch(updateUserPermissions({ id: userId, permissions: selectedPerms })).unwrap();

      toast.success("SubAdmin created successfully with permissions");
      navigate("/admin/users");
    } catch (error) {
      toast.error(error?.message || error || "Failed to create SubAdmin");
    }
  };

  // Get distinct permission names for headers (e.g., Index, Add, Edit)
  const getPermissionHeaders = () => {
    const headers = new Set();
    modulesPermissions.forEach(mod => {
      mod.permissions.forEach(p => {
        // Filter out "Check All" since we have a dedicated column for it
        const permName = p.name.toLowerCase().replace(/\s+/g, '').replace(/_/g, '').replace(/-/g, '');
        if (permName !== 'checkall') {
          headers.add(p.name);
        }
      });
    });
    return Array.from(headers);
  };

  const permissionHeaders = getPermissionHeaders();

  // Color mapping for permission types
  const getPermissionColor = (permName) => {
    const colorMap = {
      'index': 'bg-red-500',
      'export': 'bg-green-500',
      'add': 'bg-cyan-500',
      'edit': 'bg-blue-500',
      'view': 'bg-orange-500',
      'archive': 'bg-yellow-400',
      'delete': 'bg-yellow-300'
    };
    return colorMap[permName?.toLowerCase()] || 'bg-gray-400';
  };

  // Convert string to Title Case
  const toTitleCase = (str) => {
    if (!str) return '';
    
    // Handle special cases for multi-word terms
    const specialCases = {
      'dataexport': 'Data Export',
      'data export': 'Data Export',
      'data_export': 'Data Export',
      'checkall': 'Check All',
      'check all': 'Check All',
      'check_all': 'Check All',
      'jetsetter': 'JetSetter',
      'jet_setter': 'Jet Setter',
      'jet setter': 'Jet Setter',
      'airliner': 'Airliner',
      'airliners': 'Airliner',
      'sub_admin': 'Sub Admin',
      'subadmin': 'SubAdmin',
      'venders': 'Vendor',
      'vender': 'Vendor',
      'cms': 'CMS',
      'notifications': 'Messages',
      'notification': 'Messages',
      'contract_management': 'Contract Management',
      'contractmanagement': 'Contract Management',
      'account_settings': 'Account Settings',
      'accountsettings': 'Account Settings',
      'data_analysis': 'Data Analysis',
      'dataanalysis': 'Data Analysis',
      'user_management': 'User Management',
      'usermanagement': 'User Management'
    };
    
    const normalized = str.toLowerCase().trim().replace(/-/g, '_');
    if (specialCases[normalized]) {
      return specialCases[normalized];
    }
    
    // Convert to Title Case: split by spaces/underscores/hyphens, capitalize first letter of each word
    return str
      .toLowerCase()
      .trim()
      .split(/[\s_-]+/)
      .map((word) => {
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  };

  // Display name mapping - converts API permission names to Title Case display
  const getPermissionLabel = (permName) => {
    return toTitleCase(permName);
  };

  return (
    <form onSubmit={handleSubmit} className="p-6">
      <div className="bg-white p-6 rounded shadow-sm">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-xl font-semibold">Add SubAdmin
          </h1>
          <button 
            type="button" 
            onClick={() => navigate(-1)} 
            className="bg-yellow-400 px-4 py-2 rounded flex items-center gap-2 hover:bg-yellow-500 transition-colors"
          >
            <i className="fas fa-arrow-left"></i>
            Back
          </button>
        </div>

        {/* Form Grid */}
        <div className="grid grid-cols-2 gap-6">
          {/* First Name */}
          <div>
            <label className="text-sm font-medium flex items-center gap-2">
              First Name <span className="text-red-500">*</span>
              <div className="relative inline-block">
                <i 
                  className="fas fa-info-circle text-black cursor-pointer text-sm"
                  onMouseEnter={() => setTooltipVisible('firstName')}
                  onMouseLeave={() => setTooltipVisible(null)}
                ></i>
                {tooltipVisible === 'firstName' && (
                  <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-3 py-2 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-50">
                    Enter your First Name
                    <div className="absolute right-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
                  </div>
                )}
              </div>
            </label>
            <input
              name="first_name"
              placeholder="FIRST NAME"
              value={form.first_name}
              onChange={handleChange}
              className="border p-2 w-full rounded mt-1 focus:outline-none focus:ring-1 focus:ring-yellow-400"
              required
            />
          </div>

          {/* Last Name */}
          <div>
            <label className="text-sm font-medium flex items-center gap-2">
              Last Name <span className="text-red-500">*</span>
              <div className="relative inline-block">
                <i 
                  className="fas fa-info-circle text-black cursor-pointer text-sm"
                  onMouseEnter={() => setTooltipVisible('lastName')}
                  onMouseLeave={() => setTooltipVisible(null)}
                ></i>
                {tooltipVisible === 'lastName' && (
                  <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-3 py-2 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-50">
                    Enter your Last Name
                    <div className="absolute right-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
                  </div>
                )}
              </div>
            </label>
            <input
              name="last_name"
              placeholder="LAST NAME"
              value={form.last_name}
              onChange={handleChange}
              className="border p-2 w-full rounded mt-1 focus:outline-none focus:ring-1 focus:ring-yellow-400"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label className="text-sm font-medium flex items-center gap-2">
              Email <span className="text-red-500">*</span>
              <div className="relative inline-block">
                <i 
                  className="fas fa-info-circle text-black cursor-pointer text-sm"
                  onMouseEnter={() => setTooltipVisible('email')}
                  onMouseLeave={() => setTooltipVisible(null)}
                ></i>
                {tooltipVisible === 'email' && (
                  <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-3 py-2 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-50">
                    Please enter a valid email address.
                    <div className="absolute right-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
                  </div>
                )}
              </div>
            </label>
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={form.email}
              onChange={handleChange}
              className="border p-2 w-full rounded mt-1 focus:outline-none focus:ring-1 focus:ring-yellow-400"
              required
            />
          </div>

          {/* Country + Mobile */}
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-1">
              <label className="text-sm font-medium flex items-center gap-2">
                Country Code <span className="text-red-500">*</span>
                <div className="relative inline-block">
                  <i 
                    className="fas fa-info-circle text-black cursor-pointer text-sm"
                    onMouseEnter={() => setTooltipVisible('countryCode')}
                    onMouseLeave={() => setTooltipVisible(null)}
                  ></i>
                  {tooltipVisible === 'countryCode' && (
                    <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-3 py-2 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-50">
                      Select the country code by Clicking on the text box
                      <div className="absolute right-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
                    </div>
                  )}
                </div>
              </label>
              <select
                name="country_id"
                value={form.country_id}
                onChange={handleChange}
                className="border p-2 w-full rounded mt-1 focus:outline-none focus:ring-1 focus:ring-yellow-400"
                required
              >
                <option value="">Select Country</option>
                {countries && countries.length > 0 ? (
                  countries.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.phone_code} ({country.name})
                    </option>
                  ))
                ) : (
                  <>
                    <option value="1">+91 (India)</option>
                    <option value="2">+1 (US)</option>
                    <option value="3">+44 (UK)</option>
                  </>
                )}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium flex items-center gap-2">
                Mobile Number <span className="text-red-500">*</span>
                <div className="relative inline-block">
                  <i 
                    className="fas fa-info-circle text-black cursor-pointer text-sm"
                    onMouseEnter={() => setTooltipVisible('mobile')}
                    onMouseLeave={() => setTooltipVisible(null)}
                  ></i>
                  {tooltipVisible === 'mobile' && (
                    <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-3 py-2 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-50">
                      Mobile no. can only we used once
                      <div className="absolute right-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
                    </div>
                  )}
                </div>
              </label>
              <input
                name="mobile"
                placeholder="Mobile Number"
                value={form.mobile}
                onChange={handleChange}
                className="border p-2 w-full rounded mt-1 focus:outline-none focus:ring-1 focus:ring-yellow-400"
              />
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="text-sm font-medium flex items-center gap-2">
              Status <span className="text-red-500">*</span>
              <div className="relative inline-block">
                <i 
                  className="fas fa-info-circle text-black cursor-pointer text-sm"
                  onMouseEnter={() => setTooltipVisible('status')}
                  onMouseLeave={() => setTooltipVisible(null)}
                ></i>
                {tooltipVisible === 'status' && (
                  <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-3 py-2 bg-gray-900 text-white text-xs rounded whitespace-nowrap z-50">
                    With this option you can control whether user is active or not ?
                    <div className="absolute right-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
                  </div>
                )}
              </div>
            </label>
            <select
              name="status"
              value={form.status}
              onChange={handleChange}
              className="border p-2 w-full rounded mt-1 focus:outline-none focus:ring-1 focus:ring-yellow-400"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Permissions Table Section */}
        <div className="mt-10">
          <h2 className="text-base font-semibold mb-4 text-gray-800">Set Permissions</h2>

          {permissionsLoading ? (
            <div className="p-10 text-center text-gray-500 flex flex-col items-center gap-2">
              <i className="fas fa-circle-notch fa-spin text-2xl text-yellow-500"></i>
              <span>Loading permission modules...</span>
            </div>
          ) : (
            <div className="overflow-x-auto bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-white">
                    <th className="p-3 text-left font-normal text-gray-700 text-sm"></th>
                    {permissionHeaders.map((header) => (
                      <th key={header} className="p-3 text-left font-normal text-gray-700 text-xs">
                        <div className="flex items-center gap-2">
                          <span className={`w-3 h-3 rounded-sm ${getPermissionColor(header)}`}></span>
                        </div>
                      </th>
                    ))}
                    <th className="p-3 text-left font-normal text-gray-700 text-xs"></th>
                  </tr>
                </thead>

                <tbody>
                  {modulesPermissions.map((module) => (
                    <tr key={module.id} className="hover:bg-gray-50 transition-colors">
                      <td className="p-3 text-gray-700 text-sm font-semibold" style={{ textTransform: 'none' }}>{toTitleCase(module.name)}</td>

                      {permissionHeaders.map((header) => {
                        const perm = module.permissions.find(p => p.name === header);
                        const locked = perm ? isPermLocked(perm.id) : false;
                        return (
                          <td key={header} className="p-3">
                            {perm ? (
                              <label className={`flex items-center gap-2 ${locked ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`}>
                                <input
                                  type="checkbox"
                                  checked={selectedPerms.includes(Number(perm.id))}
                                  onChange={() => togglePermission(perm.id)}
                                  disabled={locked}
                                  className="w-4 h-4 rounded border-gray-300"
                                />
                                <span className="text-xs text-gray-600 font-semibold whitespace-nowrap" style={{ textTransform: 'none' }}>{getPermissionLabel(header)}</span>
                              </label>
                            ) : null}
                          </td>
                        );
                      })}

                      <td className="p-3">
                        {/* Check All — only enabled if the user owns at least one permission in this module */}
                        {(() => {
                          const ownedInModule = module.permissions.filter(p => !isPermLocked(p.id));
                          const allOwned = ownedInModule.length > 0 && ownedInModule.every(p => selectedPerms.includes(Number(p.id)));
                          const hasOwned = ownedInModule.length > 0;
                          return (
                            <label className={`flex items-center gap-2 ${hasOwned ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'}`}>
                              <input
                                type="checkbox"
                                checked={allOwned}
                                onChange={() => toggleCheckAll(module)}
                                disabled={!hasOwned}
                                className="w-4 h-4 rounded border-gray-300"
                              />
                              <span className="text-xs text-gray-600 font-semibold whitespace-nowrap" style={{ textTransform: 'none' }}>Check All</span>
                            </label>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mt-10">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={addUserLoading || updatingPermissions}
            className="bg-yellow-400 px-8 py-2 rounded font-semibold text-gray-900 hover:bg-yellow-500 disabled:opacity-50 flex items-center gap-2 transition-all shadow-sm"
          >
            {(addUserLoading || updatingPermissions) && <i className="fas fa-spinner fa-spin"></i>}
            {addUserLoading ? "Creating..." : "Create"}
          </button>
        </div>
      </div>
    </form>
  );
};

export default AddSubAdmin;